import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ArrowRight, IceCream, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

import BibleTriviaGame from './BibleTriviaGame';
import VerseScrambleGame from './VerseScrambleGame';
import WhoSaidItGame from './WhoSaidItGame';
import VerseMatchGame from './VerseMatchGame';
import BibleCrosswordGame from './BibleCrosswordGame';
import {
  fetchSundaeToday,
  startSundae,
  completeSundae,
  SUNDAE_GAME_LABELS,
} from '../../services/sundaeApi';
import { useLanguage } from '../../contexts/LanguageContext';

const GAME_COMPONENTS = {
  trivia: BibleTriviaGame,
  scramble: VerseScrambleGame,
  whosaidit: WhoSaidItGame,
  match: VerseMatchGame,
  crossword: BibleCrosswordGame,
};

const fmt = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Sundae Runner — orchestrates the daily 5-game sequence.
 *
 * Flow:
 *   1) Mount → POST /sundae/start to pin started_at + fetch state.
 *   2) Render the first un-completed game from `state.order`.
 *      Override its `onScored` to refresh state and surface a "Next" CTA.
 *      Override its `onBack` to a confirm-quit flow.
 *   3) When a game's `onScored` fires we record + advance via "Next →".
 *   4) After the 5th game's score we POST /sundae/complete and call
 *      `onAllComplete(finalState)` so the parent can show the share card.
 *
 * Timer is local (uses started_at from server). Persists across reloads
 * because started_at is server-pinned. If the user reloads mid-sundae the
 * runner picks up at next_game and the timer is rehydrated from started_at.
 */
const SundaeRunner = ({ language = 'en', onAllComplete, onQuit, onScoreRefresh }) => {
  const { t } = useLanguage();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [justScored, setJustScored] = useState(false); // user just submitted current game
  const [now, setNow] = useState(Date.now());
  const completingRef = useRef(false);

  // Boot: pin a started_at and load state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await startSundae();
        if (cancelled) return;
        // Already completed today → bail out to summary immediately so we
        // never render an empty runner with next_game=null.
        if (s.completed_at) {
          onAllComplete?.(s);
          return;
        }
        setState(s);
      } catch {
        toast.error(
          t('games.runner.couldNotStart'),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live timer.
  useEffect(() => {
    if (!state?.started_at || state?.completed_at) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [state?.started_at, state?.completed_at]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-12 text-muted-foreground"
        data-testid="sundae-loading"
      >
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('games.runner.preparing')}
      </div>
    );
  }

  if (!state) return null;

  const currentGame = state.next_game;
  const elapsedMs = state.started_at ? now - state.started_at * 1000 : 0;
  const overTime = elapsedMs > 5 * 60 * 1000;
  const total = state.games?.length || 5;
  const done = state.games?.filter((g) => g.completed).length || 0;
  const stepIdx = (state.order || []).indexOf(currentGame);

  /* ──────────── advance to next game ──────────── */
  const handleAdvance = async () => {
    setJustScored(false);
    try {
      const fresh = await fetchSundaeToday();
      onScoreRefresh?.();
      if (!fresh.next_game) {
        // All 5 done → complete.
        if (completingRef.current) return;
        completingRef.current = true;
        const finalState = await completeSundae(elapsedMs);
        onAllComplete?.(finalState);
        window.dispatchEvent(new CustomEvent('lumera:milestone-share', { detail: {
          kind: 'sundae',
          reference: '',
          verseText: '',
          message: t('games.runner.milestone'),
        }}));
        return;
      }
      setState(fresh);
    } catch {
      toast.error(t('games.runner.couldNotContinue'));
    }
  };

  /* ──────────── intercepted callbacks for each game ──────────── */
  const handleScored = () => {
    setJustScored(true);
    onScoreRefresh?.();
  };

  const handleQuit = () => {
    if (
      window.confirm(
        t('games.runner.quitConfirm'),
      )
    ) {
      onQuit?.();
    }
  };

  const Game = GAME_COMPONENTS[currentGame];

  return (
    <div className="space-y-4" data-testid="sundae-runner">
      {/* Header strip — always visible during the sundae */}
      <div className="rounded-lg border bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 flex items-center justify-between gap-3 flex-wrap shadow-md">
        <div className="flex items-center gap-2 text-sm">
          <IceCream className="w-4 h-4" />
          <span className="font-bold">
            {t('games.runner.sundae')}
          </span>
          <Badge className="bg-white/20 text-white border-white/30 text-[10px]">
            {stepIdx >= 0 ? stepIdx + 1 : done + 1}/{total}
          </Badge>
          <span className="font-medium">
            {Game ? SUNDAE_GAME_LABELS[currentGame]?.[language] : '—'}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div
            className={`flex items-center gap-1 tabular-nums font-mono ${
              overTime ? 'text-red-200' : ''
            }`}
            data-testid="sundae-timer"
          >
            <Clock className="w-3.5 h-3.5" />
            {fmt(elapsedMs)} / 5:00
          </div>
          <Button
            onClick={handleQuit}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 h-7 px-2"
            data-testid="sundae-quit-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            {t('games.runner.quit')}
          </Button>
        </div>
      </div>

      {/* Continue strip — shown after a game submission */}
      {justScored && (
        <div
          className="rounded-lg border-2 border-purple-500 bg-purple-50 dark:bg-purple-950/40 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
          data-testid="sundae-continue-strip"
        >
          <p className="text-sm font-medium">
            {t('games.runner.nice')}
          </p>
          <Button
            onClick={handleAdvance}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
            data-testid="sundae-next-btn"
          >
            {done + 1 >= total
              ? t('games.runner.seeResults')
              : t('games.runner.nextGame')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Render the current game inline. We pass our own callbacks.
          Game stays mounted after scoring so the user can review answers
          while the continue strip waits at the top. */}
      {Game && (
        <Game
          key={currentGame}
          language={language}
          onBack={handleQuit}
          onScored={handleScored}
        />
      )}
    </div>
  );
};

export default SundaeRunner;
